/**
 * Ingredient Management Page
 *
 * Lists all ingredients used in production with halal status tracking:
 * - Ingredient list with halal verification status
 * - Supplier linkage
 * - Filter by halal status
 * - Quick actions (view, edit, link to supplier)
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Select } from '../../components/forms/Select';
import type { Ingredient } from '../../types/supplier';

export const IngredientManagementPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [filteredIngredients, setFilteredIngredients] = useState<Ingredient[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadIngredients();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [ingredients, statusFilter]);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      // Mock ingredient data for Sprint 1
      // In real app, would fetch from dedicated ingredients endpoint
      const mockIngredients: any[] = [
        {
          id: 'ing-001',
          name: 'Palm Oil',
          scientificName: 'Elaeis guineensis',
          category: 'Oils',
          halalStatus: 'HALAL',
          supplierName: 'Halal Oils Trading',
          halalCertificateNumber: 'HC-2025-001',
        },
        {
          id: 'ing-002',
          name: 'Chicken Breast',
          scientificName: 'Gallus gallus domesticus',
          category: 'Meat',
          halalStatus: 'HALAL',
          supplierName: 'Halal Poultry Farm',
          halalCertificateNumber: 'HC-2025-002',
        },
        {
          id: 'ing-003',
          name: 'Modified Food Starch',
          category: 'Additives',
          halalStatus: 'PENDING_VERIFICATION',
          supplierName: 'Food Ingredients Co.',
          halalCertificateNumber: null,
        },
      ];
      setIngredients(mockIngredients);
    } catch (error) {
      console.error('Error loading ingredients:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...ingredients];

    if (statusFilter === 'HALAL') {
      filtered = filtered.filter((ing) => ing.halalStatus === 'HALAL');
    } else if (statusFilter === 'PENDING') {
      filtered = filtered.filter((ing) => ing.halalStatus === 'PENDING_VERIFICATION');
    } else if (statusFilter === 'NON_HALAL') {
      filtered = filtered.filter((ing) => ing.halalStatus === 'NON_HALAL');
    }

    setFilteredIngredients(filtered);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'success' | 'warning' | 'error'; label: string }> = {
      HALAL: { variant: 'success', label: 'Halal' },
      PENDING_VERIFICATION: { variant: 'warning', label: 'Pending' },
      NON_HALAL: { variant: 'error', label: 'Non-Halal' },
      MUSHBOOH: { variant: 'warning', label: 'Mushbooh' },
    };
    const config = statusMap[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'HALAL', label: 'Halal' },
    { value: 'PENDING', label: 'Pending Verification' },
    { value: 'NON_HALAL', label: 'Non-Halal' },
  ];

  const stats = {
    total: ingredients.length,
    verified: ingredients.filter((i) => i.halalStatus === 'HALAL').length,
    pending: ingredients.filter((i) => i.halalStatus === 'PENDING_VERIFICATION').length,
  };

  return (
    <PageLayout
      pageTitle="Ingredient Management"
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: 'Ingredients' },
      ]}
      actions={
        <Button variant="primary" onClick={() => alert('Add New Ingredient')}>
          + Add Ingredient
        </Button>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Total Ingredients</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.total}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Halal Verified</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.verified}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Pending Verification</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select
                label="Halal Status"
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
            <div className="flex items-end pb-2">
              <div className="text-sm text-gray-600">
                Showing {filteredIngredients.length} of {ingredients.length} ingredients
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Ingredients Table */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Ingredients</h3>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-8 text-gray-500">{t('common:status.loading')}...</div>
          ) : filteredIngredients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {ingredients.length === 0
                ? 'No ingredients found. Add ingredients to track their halal status.'
                : 'No ingredients match your filters'}
            </div>
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Ingredient Name',
                  render: (ing: any) => (
                    <div>
                      <div className="font-medium">{ing.name}</div>
                      <div className="text-xs text-gray-500">{ing.scientificName || 'No scientific name'}</div>
                    </div>
                  ),
                },
                {
                  key: 'supplier',
                  header: 'Supplier',
                  render: (ing: any) => (
                    <div className="text-sm">
                      <div className="text-blue-600 hover:underline cursor-pointer">{ing.supplierName}</div>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: 'Halal Status',
                  render: (ing: any) => getStatusBadge(ing.halalStatus),
                },
                {
                  key: 'category',
                  header: 'Category',
                  render: (ing: any) => (
                    <Badge variant="default" size="sm">
                      {ing.category || 'Uncategorized'}
                    </Badge>
                  ),
                },
                {
                  key: 'certificate',
                  header: 'Certificate',
                  render: (ing: any) => (
                    <div className="text-sm">
                      {ing.halalCertificateNumber || <span className="text-gray-400">N/A</span>}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (ing: any) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => alert(`View ingredient: ${ing.name}`)}
                      >
                        {t('common:actions.view')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => alert(`Edit ingredient: ${ing.name}`)}
                      >
                        {t('common:actions.edit')}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredIngredients}
              keyExtractor={(ing: any) => ing.id}
            />
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default IngredientManagementPage;

